import { useLocation } from 'react-router';
import type { PAGE_POSITIONS, Surface } from 'types';
import type {
  Affiliate,
  Price,
  ProductListItemExternal
} from 'types/capi/shared';

import type { Variant } from '~/components/Button';
import AffiliateBuyNowEvent from '~/events/AffiliateBuyNowEvent';
import ProductCardClickEvent from '~/events/ProductCardClickEvent';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import wct from '~/services/wct.client';
import type { UrlTemplates } from '~/utils/tracking';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

import Tracked from '../Tracked';

type Props = {
  affiliate: Affiliate;
  buttonPosition: number;
  children: JSX.Element | JSX.Element[] | string;
  className?: string;
  listId?: string | null;
  name?: TrackedButtonClickName;
  openPdp?: boolean;
  pagePosition: PAGE_POSITIONS;
  position?: number;
  price: Price;
  product: ProductListItemExternal;
  selectedSize?: string;
  surface?: Surface;
  urlTemplates: UrlTemplates;
  variant?: Variant;
};

export default function ClickOutLink({
  affiliate,
  buttonPosition,
  children,
  listId,
  name,
  className,
  position,
  openPdp = true,
  pagePosition,
  price,
  product,
  surface,
  urlTemplates,
  variant
}: Props) {
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot } = useRootData();
  const { pathname, search } = useLocation();

  const interstitialUrl =
    env.SITE_BASE_URL + `/co/${product.id}/${affiliate.id}`;

  const fireEvents = async (url: string, clickRef: string) => {
    if (isBot) return;

    new ProductCardClickEvent({
      isBot,
      listId,
      position,
      product,
      surface,
      user
    }).send();

    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      product,
      url
    });

    await new AffiliateBuyNowEvent({
      affiliateId: affiliate.id,
      affiliateName: affiliate.name,
      buttonPosition,
      clientIp,
      currency: price.currency,
      isBot,
      isReseller: affiliate.is_reseller,
      pagePath,
      pagePosition,
      price: price.sale ?? price.regular,
      product,
      queryID: localStorage.getItem('queryID'),
      user
    }).send();
  };

  const onClick = async (url: string, clickRef: string) => {
    if (openPdp) window.open(interstitialUrl, '_blank');

    await fireEvents(url, clickRef);
  };

  const { url, clickRef } = getTrackedUrlAndClickRef(urlTemplates, {
    buttonPosition,
    user
  });

  return (
    <Tracked.InternalLink
      variant={variant ?? 'none'}
      title={product.name}
      name={name ?? 'PRODUCT_CARD'}
      pagePosition={pagePosition}
      aria-label={product.name}
      to={openPdp ? product.path : interstitialUrl}
      onClick={() => onClick(url, clickRef)}
      className={className}
      target={openPdp ? undefined : '_blank'}
    >
      {children}
    </Tracked.InternalLink>
  );
}
